import { parse, stringify } from 'qs';
import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFallbackSearchUrl } from 'reactApp/hooks/useFallbackSearchUrl';
import { historyPush } from 'reactApp/modules/router/router.module';
import { sendSearchInputClickRadar } from 'reactApp/modules/search/search.analytics';
import { getSearchRequestParams } from 'reactApp/modules/search/search.selectors';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { getIdByStorage, getStorage } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import opener from 'reactApp/utils/opener';

import { EActions, IAction, ISearchContent, ISearchContentProps, IState, typesOptions } from '../WebSearch.data';

const reducer = (state: IState, action: IAction): IState => {
    switch (action.type) {
        case EActions.close:
            return {
                active: false,
                focus: false,
                open: false,
                value: '',
                type: 'all',
            };
        case EActions.removeType:
            return {
                ...state,
                type: 'all',
                open: false,
                focus: false,
            };
        case EActions.setFocus:
            return {
                ...state,
                open: false,
                focus: Boolean(action.payload),
            };
        case EActions.setType:
            return {
                ...state,
                open: false,
                type: String(action.payload),
            };
        case EActions.setActive:
            return {
                ...state,
                active: true,
                focus: true,
            };
        case EActions.setValue:
            return {
                ...state,
                open: false,
                value: String(action.payload),
            };
        case EActions.setOpenFilters:
            return {
                ...state,
                open: Boolean(action.payload),
            };
        case EActions.toggleFilters:
            return {
                ...state,
                open: !state.open,
            };
    }
};

// eslint-disable-next-line max-lines-per-function,sonarjs/cognitive-complexity
export const useWebSearchContent = ({
    toggleSearch,
    rootRef,
    isExpanded,
    onlyExpanded,
    storage,
    dropdownRef,
    onSearchStateChange,
}: ISearchContentProps): ISearchContent => {
    const { isAttaches, isReactPage, isSearch } = getStorage(storage);

    const { query = '', searchType = 'all' } = useSelector(SettingsSelectors.getQueryParams) || {};
    const { xPageId, xReqId } = useSelector(getSearchRequestParams);
    const { setStartLocation, closeSearch } = useFallbackSearchUrl();

    const [state, invokeDispatch] = useReducer(reducer, {
        active: Boolean(onlyExpanded),
        focus: false,
        open: false,
        value: query,
        type: searchType,
    });

    const dispatch = ({ type, payload }: { type: EActions; payload?: any }) => {
        onSearchStateChange({ type, payload });
        invokeDispatch({ type, payload });
    };

    const storeDispatch = useDispatch();

    const placeholder = isAttaches ? 'Поиск по разделу' : 'Поиск по Облаку';
    const searchOption = typesOptions[state.type];

    const showSearchPage = useCallback(() => {
        const { value, type } = state;

        if (!value && type === 'all') {
            return;
        }

        const { query: _query, searchType: _, ...rest } = parse(window.location.search, { ignoreQueryPrefix: true });
        const searchOther = stringify(rest);

        if (!isSearch) {
            setStartLocation();
        }

        if (isAttaches) {
            storeDispatch(historyPush({ id: '/attaches', search: `?query=${value}${searchOther ? `&${searchOther}` : ''}` }));
            return;
        }

        if (isReactPage) {
            storeDispatch(
                historyPush({ id: '/search/', search: `?query=${value}${searchOther ? `&${searchOther}` : ''}&searchType=${type}` })
            );
        }
    }, [isAttaches, isReactPage, isSearch, setStartLocation, state, storeDispatch]);

    const onInputClick = useCallback(() => {
        if (isExpanded || onlyExpanded) {
            dispatch({ type: EActions.setFocus, payload: true });
        } else {
            toggleSearch(true);
            sendSearchInputClickRadar({ xPageId, xReqId, storage });
        }
    }, [isExpanded, onlyExpanded, toggleSearch]);

    const onSearchClick = useCallback(() => {
        if (state.active) {
            dispatch({ type: EActions.setFocus, payload: false });
            showSearchPage();
            return;
        }

        toggleSearch(true);
    }, [showSearchPage, state.active, toggleSearch]);

    const onInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        dispatch({ type: EActions.setValue, payload: event.target?.value });
    }, []);

    const onInputClear = useCallback(() => {
        if (state.value) {
            dispatch({ type: EActions.setValue, payload: '' });
            return;
        }

        dispatch({ type: EActions.removeType });

        if (isAttaches) {
            storeDispatch(historyPush({ id: getIdByStorage(EStorageType.attaches) }));
            toggleSearch(false);
            return;
        }

        if (isReactPage) {
            closeSearch();
            toggleSearch(false);
            return;
        }

        // TODO: поправить после https://jira.vk.team/browse/CLOUDWEB-13172
        opener(getIdByStorage(EStorageType.home), true);
    }, [state.value, isReactPage, isAttaches, closeSearch, toggleSearch, storeDispatch]);

    const onDocumentClick = useCallback(
        (event: Event) => {
            let closeIt = true;
            let target = event.target as HTMLElement;
            while (target && target.parentNode !== document) {
                if (target === rootRef?.current || target === dropdownRef?.current) {
                    closeIt = false;
                    break;
                }
                target = target.parentNode as HTMLElement;
            }

            if (!closeIt) {
                return;
            }

            if (onlyExpanded) {
                dispatch({ type: EActions.setFocus, payload: false });
                return;
            }
            toggleSearch(false);
        },
        [dropdownRef, onlyExpanded, rootRef, toggleSearch]
    );

    const setActiveInput = useCallback(() => {
        dispatch({ type: EActions.setActive });
    }, []);

    const chooseSearchType = useCallback((type: string) => {
        if (typesOptions[type]) {
            dispatch({ type: EActions.setType, payload: type });
            return;
        }

        dispatch({ type: EActions.setOpenFilters, payload: false });
    }, []);

    const removeSearchType = useCallback(() => {
        dispatch({ type: EActions.removeType });
    }, []);

    const removeFocus = useCallback(() => {
        dispatch({ type: EActions.setFocus, payload: false });
    }, []);

    const onKeyUp = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                dispatch({ type: EActions.setFocus, payload: false });
                showSearchPage();
            }
        },
        [showSearchPage]
    );

    const onToggleFilters = useCallback(() => {
        dispatch({ type: EActions.toggleFilters });
    }, []);

    useEffect(() => {
        if (!isExpanded) {
            dispatch({ type: EActions.close });
        }
    }, [isExpanded]);

    useEffect(() => {
        showSearchPage();
    }, [state.type]);

    return {
        isFocus: state.focus,
        isOpen: state.open,
        value: state.value,
        searchOption,
        placeholder,
        onSearchClick,
        onInputClick,
        onInputClear,
        onDocumentClick,
        onInputChange,
        onKeyUp,
        onToggleFilters,
        setActiveInput,
        chooseSearchType,
        removeSearchType,
        removeFocus,
    };
};
